/**
 * ScreenerPanel (conversation view)
 *
 * Renders a contact's screener submission(s) from the server-built, label-resolved
 * `screener` block (crmScreener.js): one collapsible section per submission (a
 * person can submit more than once — the first/most recent is open by default,
 * the rest start collapsed), each showing study + questionnaire, an eligibility
 * badge, an "Incomplete screener" flag when eligibility_status='pending', and
 * the label->value answers. `mask` (no-op default) redacts answer values for
 * Phase 7.
 */
import { useState } from 'react';
import { ClipboardList, AlertTriangle, ChevronDown } from 'lucide-react';
import { Screener } from '../types';
import { answerHighlight, eligibilityBadge as sharedEligibilityBadge } from './shared';

interface Props {
  screener: Screener | null | undefined;
  mask?: boolean;
}

const BADGE_YELLOW = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
const BADGE_GRAY = 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

/** Per-submission badge: this panel ALWAYS shows a badge, with the extra
 *  `incomplete` flag (server-derived) forcing "Incomplete screener", and an
 *  empty/unknown status falling back to gray "Unknown". The verdict mapping
 *  itself is the shared single source of truth. */
function eligibilityBadge(status: string | null, incomplete: boolean): { label: string; cls: string } {
  if (incomplete) return { label: 'Incomplete screener', cls: BADGE_YELLOW };
  return sharedEligibilityBadge(status) || { label: 'Unknown', cls: BADGE_GRAY };
}

export default function ScreenerPanel({ screener, mask = false }: Props) {
  const submissions = screener?.submissions || [];
  // First submission open by default; the rest collapsed.
  const [open, setOpen] = useState<Record<number, boolean>>({ 0: true });

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <ClipboardList className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Screener answers
          {submissions.length > 1 && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({submissions.length} submissions)</span>
          )}
        </h3>
        {screener && screener.incompleteCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
            <AlertTriangle className="h-3 w-3" /> {screener.incompleteCount} incomplete
          </span>
        )}
      </div>

      {submissions.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No screener submissions recorded.</p>
      ) : (
        <div className="space-y-3">
          {submissions.map((s, idx) => {
            const badge = eligibilityBadge(s.eligibility_status, s.incomplete);
            const isOpen = !!open[idx];
            return (
              <div
                key={s.lead_id || idx}
                className="overflow-hidden rounded-lg border border-gray-100 dark:border-gray-700"
              >
                <button
                  onClick={() => setOpen((p) => ({ ...p, [idx]: !p[idx] }))}
                  aria-expanded={isOpen}
                  className="flex min-h-[44px] w-full items-center justify-between gap-2 p-3 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:hover:bg-gray-700/40"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-150 ${isOpen ? '' : '-rotate-90'}`}
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {s.study_internal_title || s.study_title || s.study_slug || 'Submission'}
                      </span>
                      {(s.questionnaire_name || s.first_seen_at) && (
                        <span className="block text-xs text-gray-400">
                          {s.questionnaire_name}
                          {s.questionnaire_name && s.first_seen_at ? ' · ' : ''}
                          {s.first_seen_at ? new Date(s.first_seen_at).toLocaleDateString() : ''}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.cls}`}>
                    {badge.label}
                  </span>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 px-3 pb-3 dark:border-gray-700">
                    {s.answers.length === 0 ? (
                      <p className="pt-2 text-sm text-gray-500 dark:text-gray-400">No answers recorded for this submission.</p>
                    ) : (
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {s.answers.map((a) => (
                          <div key={a.question_id} className="flex items-start justify-between gap-4 py-1.5">
                            <span className="text-sm text-gray-500 dark:text-gray-400">{a.label}</span>
                            <span className={`break-words text-right text-sm font-medium ${answerHighlight(a.eligibility_kind)}`}>
                              {mask ? '•••' : a.display}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {!mask && s.eligibility_notes && (
                      <p className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                        <span className="font-medium">Notes:</span> {s.eligibility_notes}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
